import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Route,
  UrlSegment,
  Router
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { take, switchMap, filter } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';

/**
 * Custom AuthGuard that redirects to the landing page instead of Auth0
 * This allows users to select their authentication method from the landing page
 * @feature 010-universal-landing-page
 */
@Injectable({
  providedIn: 'root',
})
export class CustomAuthGuard {
  private auth = inject(AuthService);
  private router = inject(Router);
  private appInsights = inject(AppInsightsService);

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    // Wait for Auth0 to finish loading before checking
    return this.auth.isLoading$.pipe(
      filter(isLoading => !isLoading),
      take(1),
      switchMap(() => this.auth.isAuthenticated$.pipe(take(1)))
    );
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.redirectIfUnauthenticated(state);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.redirectIfUnauthenticated(state);
  }

  /**
   * Wait for Auth0 to finish loading, then redirect to landing page if not authenticated
   * This prevents the flash of login page during auth callback processing
   */
  private redirectIfUnauthenticated(
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.auth.isLoading$.pipe(
      // Wait until Auth0 SDK has finished initializing/processing
      filter(isLoading => !isLoading),
      take(1),
      switchMap(() => this.auth.isAuthenticated$.pipe(take(1))),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          // Navigate to landing page with returnUrl to preserve deep link
          const returnUrl = state.url;
          this.router.navigate(['/'], {
            queryParams: returnUrl !== '/' ? { returnUrl } : {}
          });
          return of(false);
        }
        // Breadcrumb a successful guard pass (spec 034) so the auth-restore ->
        // guard -> resolver sequence is fully visible around the tokenless window.
        this.appInsights.trackTrace('auth_guard_pass', { url: state.url });
        return of(true);
      })
    );
  }
}
